import {
  RefObject,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

const ARROW_HALF_HEIGHT = 8

export const PopoverArrow = ({ top }: { top: number | null }) => (
  <div
    className="pointer-events-none absolute left-[-8px] z-10 h-0 w-0 border-y-[8px] border-r-[8px] border-solid border-y-transparent border-r-white"
    style={{
      top: top !== null ? `${top}px` : '50%',
      transform: top !== null ? undefined : 'translateY(-50%)',
    }}
  />
)

export const usePopoverArrowPosition = (
  triggerRef: RefObject<HTMLDivElement | null>,
  isOpen: boolean,
  isSticky: boolean,
) => {
  const contentRef = useRef<HTMLDivElement | null>(null)
  const [arrowTop, setArrowTop] = useState<number | null>(null)

  const setContentRef = useCallback(
    (el: HTMLDivElement | null) => {
      contentRef.current = el
      if (!el || !isOpen || isSticky) {
        setArrowTop(null)
        return
      }
      const measure = () => {
        if (!triggerRef.current || !contentRef.current) return
        const triggerRect = triggerRef.current.getBoundingClientRect()
        const contentRect = contentRef.current.getBoundingClientRect()
        const triggerCenterY = triggerRect.top + triggerRect.height / 2
        const top = triggerCenterY - contentRect.top - ARROW_HALF_HEIGHT
        const clamped = Math.max(
          ARROW_HALF_HEIGHT,
          Math.min(contentRect.height - 2 * ARROW_HALF_HEIGHT, top),
        )
        setArrowTop(clamped)
      }
      requestAnimationFrame(measure)
    },
    [isOpen, isSticky, triggerRef],
  )

  useLayoutEffect(() => {
    if (!isOpen || isSticky) setArrowTop(null)
  }, [isOpen, isSticky])

  return { contentRef: setContentRef, arrowTop }
}
